import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import APIService from '../services/api';
import { useEnvoiNda } from './EnvoiNda';
import { ancre } from '../tutoriel/ancres';

/**
 * UNE COUCHE ANCRÉE — le rapport se pose SUR l'écran, il ne le pousse pas.
 *
 * Vécu 21.09 : « quand je clique sur À qualifier par Sév, ça doit apparaître
 * comme un clic droit — ça ouvre le truc sans impacter la div existante ».
 * Déplié dans le flux, un panneau de 300 px repoussait la carte du candidat,
 * les boutons, et la ligne suivante : on perdait de vue ce qu'on venait de
 * regarder, et refermer était le seul moyen de retrouver sa place.
 *
 * Quatre choses qu'un menu contextuel fait et qu'un dépliage ne fait pas :
 *
 * 1. IL NE DÉPLACE RIEN. `position: absolute` dans un parent `relative` :
 *    la carte garde exactement la hauteur qu'elle avait.
 * 2. IL SE FERME OÙ QU'ON CLIQUE, et sur Échap — on l'ouvre en passant, on
 *    n'a pas à chercher comment le refermer. Le focus revient sur ce qui l'a
 *    ouvert, sinon la tabulation repart du début de la page.
 * 3. IL RESTE DANS L'ÉCRAN. Mesuré après ouverture : s'il dépasse à droite il
 *    s'aligne à droite, s'il dépasse en bas il passe au-dessus de son ancre.
 *    Un panneau à moitié hors de la fenêtre est un panneau illisible, et
 *    c'est le cas normal pour une pastille en bas de liste.
 * 4. IL NE DÉBORDE PAS EN HAUTEUR : au-delà, il défile tout seul.
 *
 * L'élévation est la seule du système (`--hover-shadow`) : ce qui flotte doit
 * se lire comme flottant, sinon on croit que la page a bougé.
 */
function CoucheAncree({ children, onFermer, libelle }) {
  const boite = useRef(null);
  const ouvreur = useRef(null);
  const [place, setPlace] = useState({ droite: false, dessus: false });

  // Retenir QUI avait le focus avant l'ouverture, pour le lui rendre.
  useEffect(() => {
    ouvreur.current = document.activeElement;
    return () => { try { ouvreur.current?.focus?.(); } catch { /* élément parti */ } };
  }, []);

  // Le placement se MESURE, il ne se devine pas : la même pastille est en
  // haut de l'écran sur le premier candidat et en bas sur le dernier.
  useLayoutEffect(() => {
    const el = boite.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const marge = 8;
    setPlace({
      droite: r.right > window.innerWidth - marge,
      dessus: r.bottom > window.innerHeight - marge && r.top > r.height + marge,
    });
  }, []);

  useEffect(() => {
    const dehors = (e) => { if (boite.current && !boite.current.contains(e.target)) onFermer?.(); };
    const touche = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onFermer?.(); } };
    // `mousedown` et pas `click` : un clic qui commence dehors et finit
    // dedans (sélection de texte relâchée sur le panneau) ne doit pas fermer.
    document.addEventListener('mousedown', dehors, true);
    document.addEventListener('keydown', touche, true);
    return () => {
      document.removeEventListener('mousedown', dehors, true);
      document.removeEventListener('keydown', touche, true);
    };
  }, [onFermer]);

  return (
    <div ref={boite} role="dialog" aria-label={libelle} tabIndex={-1}
      style={{
        position: 'absolute', zIndex: 60,
        ...(place.droite ? { right: 0 } : { left: 0 }),
        ...(place.dessus ? { bottom: 'calc(100% + 6px)' } : { top: 'calc(100% + 6px)' }),
        minWidth: '300px', width: 'max-content', maxWidth: 'min(520px, calc(100vw - 32px))',
        maxHeight: 'min(70vh, 560px)', overflowY: 'auto', overscrollBehavior: 'contain',
        borderRadius: '12px', background: 'var(--panel)', border: '1px solid var(--border)',
        boxShadow: '0 12px 40px var(--hover-shadow)',
        textAlign: 'left', cursor: 'default',
      }}>
      {/* Un panneau flottant se ferme AUSSI par un bouton : le clic dehors
          est une habitude, pas une évidence pour tout le monde. */}
      <button type="button" onClick={onFermer} aria-label="Fermer le rapport"
        style={{ position: 'absolute', top: '7px', right: '7px', zIndex: 1, lineHeight: 1,
          padding: '3px 6px', borderRadius: '7px', border: '1px solid var(--hair)',
          background: 'var(--panel)', color: 'var(--text-3)', cursor: 'pointer', font: 'inherit', fontSize: '11px' }}>
        <BI icon="x-lg" />
      </button>
      {children}
    </div>
  );
}

/**
 * QUALIFIER UN PROFIL — le contrôle, côté écran.
 *
 * Demande 16.09 : « quand je passe un profil à qualifier, je choisis par qui ;
 * cette personne reçoit une notification, arrive sur le profil, un popup lui
 * demande de répondre maintenant ou plus tard ; si maintenant, un process
 * s'ouvre […] et là il passe qualifié (qualifié par le user X) ».
 *
 * TOUT VIENT DU SERVEUR : les étapes, ce que chacune change, ce qu'il reste à
 * faire, et le droit de conclure. L'écran n'en code aucune — une étape ajoutée
 * côté serveur apparaît ici sans redéploiement, et surtout : une règle de
 * contrôle recopiée dans l'écran serait une règle qu'on contourne en appelant
 * la route à la main.
 */
const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });


const nomDe = (u) => u ? (`${u.prenom || ''} ${u.nom || ''}`.trim() || u.username || null) : null;
const nomDans = (liste, id) => nomDe((liste || []).find(u => Number(u.id) === Number(id)));

/**
 * L'annuaire, chargé par le composant lui-même.
 *
 * « Faire qualifier » doit pouvoir se poser sur n'importe quelle carte de
 * candidat sans qu'on lui passe la liste des collègues : un bouton qui a
 * besoin d'un tuyau supplémentaire ne se pose qu'à un endroit. La liste
 * passée en prop reste prioritaire quand l'écran l'a déjà.
 */
const useEquipe = (fournie, actif) => {
  const combien = fournie ? fournie.length : 0;
  const [liste, setListe] = useState([]);
  useEffect(() => {
    if (combien || !actif) return;             // déjà fournie, ou personne ne regarde
    let annule = false;
    APIService.getUsers()
      .then(d => { if (!annule) setListe((d.users || []).filter(u => u.active !== false && u.role !== 'ressource' && u.ressource !== true && !/#supprime-/.test(String(u.username || '')))); })
      .catch(() => { /* annuaire indisponible : le choix reste vide, et le refus viendra du serveur */ });
    return () => { annule = true; };
  }, [actif, combien]);
  return combien ? fournie : liste;
};

const btn = { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' };
const btnFort = { ...btn, background: 'var(--erp-accent)', borderColor: 'var(--erp-accent)', color: 'var(--erp-on-accent)' };

/**
 * LE GESTE D'UNE ÉTAPE — « depuis là je peux envoyer le NDA, mettre le CR ».
 *
 * Le serveur dit quoi proposer et OÙ ça se fait ; l'écran ne décide rien :
 *
 *  - `ou: 'ici'`    → ça se fait dans le panneau. Une SAISIE déplie ses champs
 *                     (déclarés par le serveur, pré-remplis avec ce qui est
 *                     déjà là) et écrit sur la route du couple — la même que
 *                     le bouton d'origine, donc les mêmes refus : date
 *                     ambiguë, clé inconnue.
 *  - `ou: 'fiche'`  → le bouton existe déjà sur la fiche ouverte (Appeler).
 *  - `ou: 'besoin'` → c'est un écran du besoin. On y emmène. Refaire l'éditeur
 *                     de CV ici, ce serait le deuxième chemin qui produit un
 *                     document hors charte.
 *
 * Une action qu'un module éteint rend impossible n'est pas un bouton grisé
 * sans explication : le serveur dit ce qui manque et par quoi le remplacer.
 */
/**
 * DÉPOSER UNE PIÈCE DEPUIS LE PROCESS — les réponses de prise de référence.
 *
 * Le LIBELLÉ est le sujet, pas un détail : le serveur remplace un document à
 * libellé égal, donc « Référence » tout court écraserait le référent
 * précédent. Le préfixe vient du serveur (`prefixe_libelle`), on préremplit
 * avec, et on demande de le compléter — un document par référent.
 */
function DepotEtape({ action: a, demande, showNotification, onFait }) {
  const [ouvert, setOuvert] = useState(false);
  const [libelle, setLibelle] = useState(a.prefixe_libelle || '');
  const [fichier, setFichier] = useState(null);
  const [busy, setBusy] = useState(false);

  const deposer = async () => {
    const nom = String(libelle || '').trim();
    if (!nom || !fichier) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('label', nom.slice(0, 120));
      fd.append('kind', 'uploaded');
      fd.append('file', fichier);
      const r = await fetch(`${BASE}/besoins/${demande.besoin_id}/candidates/${demande.candidate_id}/documents`, {
        method: 'POST', headers: authH(), body: fd,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      // Le serveur DIT quand il a remplacé un document de même libellé : on
      // répète son message plutôt que d'annoncer un ajout qui n'en est pas un.
      showNotification?.(j.message || `« ${nom} » déposé.`, 'success');
      setOuvert(false); setFichier(null); setLibelle(a.prefixe_libelle || '');
      await onFait?.();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (!ouvert) {
    return (
      <button style={{ ...btn, flex: 'none', padding: '4px 9px', fontSize: '11.5px' }} onClick={() => setOuvert(true)}>
        <BI icon="paperclip" /> {a.libelle}
      </button>
    );
  }

  const champStyle = { width: '100%', boxSizing: 'border-box', fontSize: '12.5px', fontFamily: 'inherit', padding: '7px 9px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--panel)', color: 'var(--text)' };
  return (
    <div style={{ flex: '1 1 260px', minWidth: 0, border: '1px solid var(--border)', borderRadius: '10px', padding: '10px', background: 'var(--panel)' }}>
      {a.aide_depot && (
        <p style={{ margin: '0 0 8px', fontSize: '11px', color: 'var(--text-3)', lineHeight: 1.5 }}>{a.aide_depot}</p>
      )}
      <label htmlFor="q-depot-libelle" style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '4px' }}>
        De qui vient cette référence
      </label>
      <input id="q-depot-libelle" value={libelle} placeholder={a.exemple_libelle || 'Référence — Nom, Société'}
        onChange={e => setLibelle(e.target.value)} style={{ ...champStyle, marginBottom: '8px' }} />
      <input type="file" aria-label="Fichier de la réponse de référence"
        onChange={e => setFichier(e.target.files?.[0] || null)}
        style={{ ...champStyle, padding: '6px 8px', marginBottom: '9px' }} />
      <div style={{ display: 'flex', gap: '7px' }}>
        <button style={{ ...btnFort, padding: '5px 11px', fontSize: '11.5px', opacity: (!fichier || !libelle.trim()) ? 0.5 : 1 }}
          disabled={busy || !fichier || !libelle.trim()} onClick={deposer}>
          {busy ? <><BI icon="arrow-repeat" /> Dépôt…</> : (a.valider || 'Déposer')}
        </button>
        <button style={{ ...btn, padding: '5px 11px', fontSize: '11.5px' }} onClick={() => { setOuvert(false); setFichier(null); }}>Annuler</button>
      </div>
    </div>
  );
}

/**
 * DÉCLARER UNE ÉTAPE SANS OBJET — avec sa raison, jamais sans.
 *
 * Le serveur REFUSE une dérogation sans motif : on demande donc le motif
 * AVANT d'envoyer, plutôt que d'afficher son refus. Une dérogation sans
 * raison est une case qu'on décoche pour aller plus vite, et six mois plus
 * tard personne ne sait pourquoi.
 */
function SansObjetEtape({ etape, demande, showNotification, onFait }) {
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const [busy, setBusy] = useState(false);

  const envoyer = async () => {
    const raison = motif.trim();
    if (!raison) return;
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/qualifications/${demande.id}/etape`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ etape: etape.cle, sans_objet: true, motif: raison }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || `« ${etape.libelle} » déclarée sans objet.`, 'success');
      setOuvert(false); setMotif('');
      await onFait?.();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (!ouvert) {
    return (
      <button style={{ ...btn, flex: 'none', padding: '4px 9px', fontSize: '11.5px' }}
        title="Cette étape n'a pas lieu d'être sur ce dossier" onClick={() => setOuvert(true)}>
        <BI icon="dash-circle" /> Sans objet
      </button>
    );
  }

  return (
    <div style={{ flex: '1 1 260px', minWidth: 0, border: '1px solid var(--border)', borderRadius: '10px', padding: '10px', background: 'var(--panel)' }}>
      <label htmlFor={`q-so-${etape.cle}`} style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '4px' }}>
        Pourquoi cette étape est sans objet *
      </label>
      <input id={`q-so-${etape.cle}`} value={motif} autoFocus
        placeholder="Poste fonctionnel, aucun test au catalogue"
        onChange={e => setMotif(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && motif.trim()) envoyer(); }}
        style={{ width: '100%', boxSizing: 'border-box', fontSize: '12.5px', fontFamily: 'inherit', padding: '7px 9px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--panel)', color: 'var(--text)', marginBottom: '9px' }} />
      <div style={{ display: 'flex', gap: '7px' }}>
        <button style={{ ...btnFort, padding: '5px 11px', fontSize: '11.5px', opacity: motif.trim() ? 1 : 0.5 }}
          disabled={busy || !motif.trim()} onClick={envoyer}>
          {busy ? <><BI icon="arrow-repeat" /> Enregistrement…</> : 'Déclarer sans objet'}
        </button>
        <button style={{ ...btn, padding: '5px 11px', fontSize: '11.5px' }} onClick={() => { setOuvert(false); setMotif(''); }}>Annuler</button>
      </div>
    </div>
  );
}

function GesteEtape({ etape, demande, showNotification, onFait }) {
  const a = etape.action;
  const [ouvert, setOuvert] = useState(false);
  const [saisie, setSaisie] = useState(null);
  const [busy, setBusy] = useState(false);
  const nda = useEnvoiNda({
    candidateId: demande.candidate_id, besoinId: demande.besoin_id,
    showNotification, onEnvoye: onFait,
  });

  if (!a) return null;

  if (a.indisponible) {
    return <span style={{ flex: 'none', maxWidth: '230px', fontSize: '11px', color: 'var(--text-3)', lineHeight: 1.45 }}>{a.indisponible}</span>;
  }

  // ── Ça se fait ailleurs : on y emmène, en disant pourquoi ───────────────
  if (a.ou === 'besoin') {
    return (
      <a href={`/besoins/${demande.besoin_id}`} title={a.pourquoi}
        style={{ ...btn, flex: 'none', padding: '4px 9px', fontSize: '11.5px', textDecoration: 'none' }}>
        {a.libelle} <BI icon="arrow-right-short" />
      </a>
    );
  }
  if (a.ou === 'fiche') {
    // Le bouton « Appeler » est sur cette fiche, juste au-dessus : le dire
    // vaut mieux qu'un deuxième bouton qui ferait la même chose ailleurs.
    return <span style={{ flex: 'none', fontSize: '11px', color: 'var(--text-3)' }}>Bouton « Appeler » en haut de la fiche</span>;
  }

  // ── Le NDA : le module partagé, avec sa demande d'adresse ───────────────
  if (a.type === 'envoi') {
    return (
      <>
        <button style={{ ...btn, flex: 'none', padding: '4px 9px', fontSize: '11.5px' }} disabled={nda.occupe}
          onClick={nda.ouvrir}>
          <BI icon={nda.occupe ? 'arrow-repeat' : 'vector-pen'} /> {nda.occupe ? 'En préparation…' : a.libelle}
        </button>
        {nda.panneau}
      </>
    );
  }

  // ── Le DÉPÔT : une pièce du positionnement (les réponses de référence) ──
  // Elle part par la route des documents du besoin — celle que le bouton
  // « Documents » appelle déjà. Une seconde route de dépôt, c'est un second
  // endroit où un contrôle manquera.
  if (a.type === 'depot') {
    return <DepotEtape action={a} demande={demande} showNotification={showNotification} onFait={onFait} />;
  }

  // ── La saisie : les champs du serveur, écrits sur la route du couple ────
  if (a.type !== 'saisie') return null;
  const valeurs = saisie ?? (a.valeurs || {});
  const set = (cle, v) => setSaisie({ ...valeurs, [cle]: v });
  const manqueRequis = (a.champs || []).some(c => c.requis && !String(valeurs[c.cle] || '').trim());

  const enregistrer = async () => {
    setBusy(true);
    try {
      // La route du COUPLE — celle que le bouton d'origine appelle déjà. Une
      // seconde route d'écriture, c'est un second endroit où un contrôle
      // manquera.
      const corps = {};
      for (const c of (a.champs || [])) {
        const v = String(valeurs[c.cle] ?? '').trim();
        if (v !== String((a.valeurs || {})[c.cle] ?? '').trim()) corps[c.cle] = v;
      }
      if (!Object.keys(corps).length) { setOuvert(false); setBusy(false); return; }
      const r = await fetch(`${BASE}/candidates/${demande.candidate_id}/besoins/${demande.besoin_id}`, {
        method: 'PATCH', headers: jsonH(), body: JSON.stringify(corps),
      });
      const j = await r.json().catch(() => ({}));
      // Le serveur refuse une date ambiguë ou une clé inconnue en DISANT
      // laquelle : son texte fait foi, on ne le résume pas.
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.('Enregistré.', 'success');
      setOuvert(false); setSaisie(null);
      await onFait?.();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  if (!ouvert) {
    return (
      <button style={{ ...btn, flex: 'none', padding: '4px 9px', fontSize: '11.5px' }} onClick={() => setOuvert(true)}>
        <BI icon="pencil" /> {a.libelle}
      </button>
    );
  }

  const champStyle = { width: '100%', boxSizing: 'border-box', fontSize: '12.5px', fontFamily: 'inherit', padding: '7px 9px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--panel)', color: 'var(--text)' };
  return (
    <div style={{ flex: '1 1 260px', minWidth: 0, border: '1px solid var(--border)', borderRadius: '10px', padding: '10px', background: 'var(--panel)' }}>
      {(a.champs || []).map(c => (
        <div key={c.cle} style={{ marginBottom: '8px' }}>
          <label htmlFor={`q-${etape.cle}-${c.cle}`} style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '4px' }}>
            {c.libelle}{c.requis ? ' *' : ''}
          </label>
          {c.type === 'texte_long' ? (
            <textarea id={`q-${etape.cle}-${c.cle}`} rows={c.lignes || 5} value={valeurs[c.cle] || ''} placeholder={c.exemple}
              onChange={e => set(c.cle, e.target.value)} style={champStyle} />
          ) : (
            <input id={`q-${etape.cle}-${c.cle}`} type={c.type === 'date' ? 'date' : 'text'} value={valeurs[c.cle] || ''} placeholder={c.exemple}
              onChange={e => set(c.cle, e.target.value)} style={champStyle} />
          )}
        </div>
      ))}
      <div style={{ display: 'flex', gap: '7px' }}>
        <button style={{ ...btnFort, padding: '5px 11px', fontSize: '11.5px', opacity: manqueRequis ? 0.5 : 1 }} disabled={busy || manqueRequis}
          onClick={enregistrer}>
          {busy ? <><BI icon="arrow-repeat" /> Enregistrement…</> : (a.valider || 'Enregistrer')}
        </button>
        <button style={{ ...btn, padding: '5px 11px', fontSize: '11.5px' }} onClick={() => { setOuvert(false); setSaisie(null); }}>Annuler</button>
      </div>
    </div>
  );
}


/**
 * LE POPUP, puis LE PROCESS — sur la fiche d'un candidat.
 *
 * S'affiche uniquement si une demande est adressée à MOI sur CE candidat :
 * c'est la suite du clic sur la notification. Personne d'autre ne le voit —
 * un process de qualification ouvert chez quelqu'un qui n'est pas concerné
 * n'aurait aucun sens.
 */
export function QualificationCandidat({ candidateId, onChange, showNotification }) {
  const [demande, setDemande] = useState(null);   // ma demande sur ce candidat
  const [etat, setEtat] = useState(null);         // { etapes, complet, restantes }
  const [ouvert, setOuvert] = useState(false);    // le process est déplié
  const [popup, setPopup] = useState(false);      // le petit popup d'arrivée
  const [busy, setBusy] = useState(null);
  const [motif, setMotif] = useState('');
  const [refus, setRefus] = useState(false);
  const [prevenir, setPrevenir] = useState(false);

  const charger = useCallback(async () => {
    if (candidateId == null) return;
    try {
      const r = await fetch(`${BASE}/qualifications/mes-demandes`, { headers: authH() });
      if (!r.ok) return;                       // route absente : rien ne s'affiche, rien ne casse
      const j = await r.json().catch(() => ({}));
      const mienne = (j.demandes || []).find(d => Number(d.candidate_id) === Number(candidateId));
      setDemande(mienne || null);
      if (!mienne) return;
      // Pas encore répondu → le popup s'ouvre tout seul : c'est l'arrivée
      // depuis la notification.
      if (mienne.statut === 'demandee') setPopup(true); else setOuvert(true);
      const e = await fetch(`${BASE}/besoins/${mienne.besoin_id}/candidates/${candidateId}/qualification`, { headers: authH() });
      if (e.ok) setEtat(await e.json().catch(() => null));
    } catch { /* silencieux : la qualification est un plus, pas la fiche */ }
  }, [candidateId]);
  useEffect(() => { charger(); }, [charger]);

  const appel = async (url, body, quoi) => {
    setBusy(quoi);
    try {
      const r = await fetch(url, { method: 'POST', headers: jsonH(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      // Le message du serveur, mot pour mot : c'est lui qui dit ce qui manque.
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      if (j.message) showNotification?.(j.message, 'success');
      await charger();
      onChange?.(j);
      return j;
    } catch (e) { showNotification?.(e.message, 'error'); return null; }
    finally { setBusy(null); }
  };

  if (!demande) return null;

  // ── LE POPUP D'ARRIVÉE ───────────────────────────────────────────────────
  if (popup) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 950, padding: '24px' }}>
        <div style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '440px', padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '6px' }}>
            <BI icon="clipboard-check" style={{ color: 'var(--brand)', fontSize: '17px' }} />
            <span style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--text-strong)' }}>Vous devez qualifier ce profil</span>
          </div>
          <p style={{ fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.6, margin: '0 0 4px' }}>
            Une demande de qualification vous a été adressée.
            {demande.message ? <> Le mot qui l'accompagne : « {demande.message} »</> : null}
          </p>
          <p style={{ fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.55, margin: '0 0 16px' }}>
            Répondre plus tard ne ferme pas la demande : elle reste sur votre liste.
          </p>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button style={btn} disabled={busy === 'tard'}
              onClick={async () => { await appel(`${BASE}/qualifications/${demande.id}/repondre`, { quand: 'plus_tard' }, 'tard'); setPopup(false); }}>
              Plus tard
            </button>
            <button style={btnFort} disabled={busy === 'now'}
              onClick={async () => { await appel(`${BASE}/qualifications/${demande.id}/repondre`, { quand: 'maintenant' }, 'now'); setPopup(false); setOuvert(true); }}>
              <BI icon="play-fill" /> Qualifier maintenant
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── LE PROCESS ───────────────────────────────────────────────────────────
  const etapes = etat?.etapes || [];
  // Une étape SANS OBJET sort du compte : « 4/4 » dit que le dossier est
  // complet, « 5/6 » ferait chercher une étape qui n'existe pas sur ce poste.
  // Même règle que le serveur — deux façons de compter finissent par se
  // contredire à l'écran.
  const comptees = etapes.filter(e => !e.sans_objet);
  const faites = comptees.filter(e => e.acquise).length;

  return (
    <div {...ancre('qualification-process')} style={{ border: '1px solid var(--brand)', borderRadius: '12px', padding: '14px', marginBottom: '14px', background: 'var(--erp-accent-soft)' }}>
      <button type="button" onClick={() => setOuvert(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '9px', width: '100%', background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
        <BI icon="clipboard-check" style={{ color: 'var(--brand)' }} />
        <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)' }}>
          Qualification à faire — {faites}/{comptees.length}
        </span>
        {demande.reportee_a && (
          <span style={{ fontSize: '10.5px', color: 'var(--text-3)' }}>reporté au {new Date(demande.reportee_a).toLocaleDateString('fr-FR')}</span>
        )}
        <BI icon={ouvert ? 'chevron-up' : 'chevron-down'} style={{ color: 'var(--text-3)' }} />
      </button>

      {ouvert && (<div style={{ marginTop: '11px' }}>
        {etapes.map(e => (
          <div key={e.cle} style={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: '9px', padding: '8px 0', borderTop: '1px solid var(--hair)' }}>
            {/* Sans objet n'est PAS une coche verte : l'étape n'a pas eu lieu.
                Le même symbole pour les deux raconterait un test qui n'a
                jamais été passé. */}
            <BI icon={e.sans_objet ? 'dash-circle' : e.acquise ? 'check-circle-fill' : 'circle'}
              style={{ color: e.sans_objet ? 'var(--text-faint)' : e.acquise ? 'var(--erp-success)' : 'var(--text-faint)', marginTop: '2px', flex: 'none' }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: '12.5px', fontWeight: 600, color: e.sans_objet ? 'var(--text-2)' : 'var(--text-strong)' }}>
                {e.libelle}
                {e.sans_objet && <span style={{ fontWeight: 400, color: 'var(--text-3)' }}> — sans objet</span>}
              </span>
              <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-3)', lineHeight: 1.5 }}>
                {e.sans_objet
                  ? <>Déclarée sans objet{e.sans_objet.motif ? ` — ${e.sans_objet.motif}` : ''}.</>
                  : e.aide}
                {/* Le CV existe mais n'a pas été relu : c'est le cas qui a
                    motivé la demande (« validé par la recruteuse »). */}
                {/* Relu PUIS régénéré : ce qui partirait n'est plus ce qu'on a
                    lu. Le serveur le dit (24.09) ; l'écran le répète tel quel. */}
                {e.validation_requise && e.fait && !e.validee && (e.relu_perime
                  ? <> <b>Il a changé depuis votre relecture : relisez-le.</b></>
                  : <> <b>Il reste à le relire.</b></>)}
                {!e.sans_objet && e.coche_a_la_main && <> Coché à la main{e.coche_a_la_main.note ? ` — ${e.coche_a_la_main.note}` : ''}.</>}
              </span>
            </span>
            {e.validation_requise && e.fait && !e.validee && (
              <button style={{ ...btnFort, flex: 'none', padding: '4px 9px', fontSize: '11.5px' }} disabled={busy === e.cle}
                onClick={() => appel(`${BASE}/qualifications/${demande.id}/etape`, { etape: e.cle, valide: true }, e.cle)}>
                J'ai relu — valider
              </button>
            )}
            {/* LE GESTE — ce qui manquait : constater qu'une étape n'est pas
                faite sans pouvoir la faire renvoie chercher le bouton
                ailleurs, et on ne revient pas finir la qualification. */}
            {!e.acquise && (
              <GesteEtape etape={e} demande={demande} showNotification={showNotification} onFait={charger} />
            )}
            {!e.fait && !e.sans_objet && e.manuel && (
              <button style={{ ...btn, flex: 'none', padding: '4px 9px', fontSize: '11.5px' }} disabled={busy === e.cle}
                title="Cocher à la main — votre nom et la date sont enregistrés"
                onClick={() => appel(`${BASE}/qualifications/${demande.id}/etape`, { etape: e.cle, coche: true }, e.cle)}>
                C'est fait
              </button>
            )}
            {/* SANS OBJET — « parfois pas besoin de faire le test » (17.09).
                Chemin séparé de « C'est fait », et c'est le fond : cocher
                « Test technique » sur un poste sans test raconte un test qui
                n'a pas eu lieu. Seules les étapes que le SERVEUR déclare
                dérogeables l'affichent. */}
            {e.derogeable && !e.fait && !e.sans_objet && (
              <SansObjetEtape etape={e} demande={demande} showNotification={showNotification} onFait={charger} />
            )}
            {e.sans_objet && (
              <button style={{ ...btn, flex: 'none', padding: '4px 9px', fontSize: '11.5px' }} disabled={busy === e.cle}
                title="Redemander cette étape sur ce dossier"
                onClick={() => appel(`${BASE}/qualifications/${demande.id}/etape`, { etape: e.cle, sans_objet: false }, e.cle)}>
                Rétablir
              </button>
            )}
          </div>
        ))}

        {!etat?.complet && etat?.restantes?.length > 0 && (
          <p style={{ fontSize: '11.5px', color: 'var(--text-3)', margin: '10px 0 0', lineHeight: 1.5 }}>
            Il reste : {etat.restantes.join(', ')}.
          </p>
        )}

        {/* PRÉVENIR LE RESPONSABLE DU BESOIN — proposé, jamais coché d'office.
            Un mail qui part chez un commercial sans que personne ne l'ait
            voulu est un mail qu'on finit par ne plus lire. */}
        {etat?.complet && (
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '13px', cursor: 'pointer' }}>
            <input type="checkbox" checked={prevenir} onChange={e => setPrevenir(e.target.checked)}
              style={{ marginTop: '2px', accentColor: 'var(--erp-accent)' }} />
            <span style={{ fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.5 }}>
              Prévenir le responsable de ce besoin — il recevra un email et une notification.
            </span>
          </label>
        )}

        <div {...ancre('qualification-verdict')} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '13px' }}>
          <button style={{ ...btnFort, opacity: etat?.complet ? 1 : 0.5 }} disabled={!etat?.complet || busy === 'verdict'}
            title={etat?.complet ? 'Toutes les étapes sont faites' : 'Il reste des étapes'}
            onClick={() => appel(`${BASE}/qualifications/${demande.id}/conclure`, { verdict: 'qualifie', prevenir_responsable: prevenir }, 'verdict')}>
            <BI icon="patch-check-fill" /> Qualifier ce profil
          </button>
          <button style={btn} onClick={() => setRefus(r => !r)}>Ne pas retenir</button>
        </div>

        {refus && (
          <div style={{ marginTop: '10px' }}>
            <textarea value={motif} onChange={e => setMotif(e.target.value)} rows={2}
              placeholder="Pourquoi ce profil n'est pas retenu — c'est ce qu'on relira"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: '12.5px', fontFamily: 'inherit', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--panel)', color: 'var(--text)' }} />
            <button style={{ ...btn, marginTop: '7px' }} disabled={!motif.trim() || busy === 'refus'}
              onClick={() => appel(`${BASE}/qualifications/${demande.id}/conclure`, { verdict: 'refuse', motif, prevenir_responsable: prevenir }, 'refus')}>
              Écarter ce profil
            </button>
          </div>
        )}
      </div>)}
    </div>
  );
}

/**
 * DEMANDER une qualification — le geste de celui qui contrôle.
 *
 * Sur la carte d'un candidat dans un besoin : « à qualifier par qui ? ».
 * L'état courant s'affiche aussi ici : qui a été saisi, et ce qu'il en a dit —
 * sans ça, on redemanderait à quelqu'un d'autre sans savoir.
 */
export function DemanderQualification({ candidateId, besoinId, equipe = [], showNotification, onChange }) {
  const [etat, setEtat] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [qui, setQui] = useState('');
  const [mot, setMot] = useState('');
  const [busy, setBusy] = useState(false);
  const [rapport, setRapport] = useState(false);  // pastille dépliée
  const annuaire = useEquipe(equipe, ouvert);

  const charger = useCallback(async () => {
    if (candidateId == null || besoinId == null) return;
    try {
      const r = await fetch(`${BASE}/besoins/${besoinId}/candidates/${candidateId}/qualification`, { headers: authH() });
      if (!r.ok) { setEtat({ absent: true }); return; }
      setEtat(await r.json().catch(() => null));
    } catch { setEtat({ absent: true }); }
  }, [candidateId, besoinId]);
  useEffect(() => { charger(); }, [charger]);

  if (!etat || etat.absent) return null;
  const d = etat.demande;

  // LA PASTILLE OUVRE LE RAPPORT. C'était un `<span>` inerte : on lisait
  // « Qualifié par François » sur la fiche du besoin sans aucun moyen de voir
  // ce qu'il avait constaté (vécu 17.09 : « et là comment tu peux voir la
  // qualification sur le besoin ? »). Le résultat sans son contenu oblige à
  // faire confiance — ce qui est exactement ce que la fonctionnalité voulait
  // éviter.
  //
  // IL S'OUVRE AU-DESSUS, PAS DEDANS (vécu 21.09 : « quand je clique sur À
  // qualifier par Sév, ça doit apparaître comme un clic droit — ça ouvre le
  // truc sans impacter la div existante »). Déplié dans le flux, le rapport
  // poussait la carte du candidat de 300 px : la ligne qu'on venait de
  // regarder partait sous le pli, et refermer était la seule façon de
  // retrouver sa place. Un rapport qu'on consulte en passant est une COUCHE
  // — comme un menu contextuel : il se pose sur l'écran, il ne le déforme pas.
  const pastille = (contenu, style) => (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'flex-start' }}>
      <button type="button" onClick={() => setRapport(o => !o)} aria-expanded={rapport}
        aria-haspopup="dialog"
        title="Voir le rapport de qualification"
        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600,
          padding: '2px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer', font: 'inherit', ...style }}>
        {contenu}
        <BI icon={rapport ? 'chevron-up' : 'chevron-down'} style={{ fontSize: '9px', opacity: 0.7 }} />
      </button>
      {rapport && (
        <CoucheAncree onFermer={() => setRapport(false)} libelle="Rapport de qualification">
          <RapportQualification id={d.id} showNotification={showNotification} encadre={false}
            onChange={() => { charger(); onChange?.(); }} />
        </CoucheAncree>
      )}
    </span>
  );

  // Déjà qualifié (ou écarté) : c'est le résultat qu'on affiche, signé.
  if (d && !d.en_cours && d.verdict) {
    const par = d.terminee_par_nom || nomDans(equipe, d.terminee_par);
    return pastille(
      <>
        <BI icon={d.verdict === 'qualifie' ? 'patch-check-fill' : 'slash-circle'} />
        {d.verdict_libelle}{par ? ` par ${par}` : ''}
        {/* CE QUI ATTEND ENCORE QUELQU'UN : « qualifié » n'est pas « parti »
            tant que le demandeur n'a pas dit ok. */}
        {d.attente_retour && <span style={{ fontWeight: 700 }}> · à valider</span>}
      </>,
      { background: d.verdict === 'qualifie' ? 'var(--erp-success-soft)' : 'var(--chip-bg)',
        color: d.verdict === 'qualifie' ? 'var(--erp-success)' : 'var(--text-2)' });
  }

  // Demande en cours : on dit à QUI, pour que personne n'en lance une seconde.
  if (d?.en_cours) {
    const nom = d.qualifieur_nom || nomDans(equipe, d.qualifieur_id) || 'quelqu\'un de l\'équipe';
    return pastille(
      <><BI icon="hourglass-split" /> À qualifier par {nom}</>,
      { background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)' });
  }

  if (!ouvert) {
    return (
      <button type="button" style={{ ...btn, padding: '4px 10px', fontSize: '11.5px' }} onClick={() => setOuvert(true)}>
        <BI icon="clipboard-check" /> Faire qualifier
      </button>
    );
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '11px', background: 'var(--ghost-bg)', minWidth: '260px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '7px' }}>Qualifier par</div>
      <select value={qui} onChange={e => setQui(e.target.value)}
        style={{ width: '100%', fontSize: '12.5px', fontFamily: 'inherit', padding: '6px 8px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--panel)', color: 'var(--text)' }}>
        <option value="">Choisir une personne…</option>
        {annuaire.map(u => (
          <option key={u.id} value={u.id}>{nomDe(u) || `#${u.id}`}</option>
        ))}
      </select>
      <input value={mot} onChange={e => setMot(e.target.value)} placeholder="Un mot pour elle (facultatif)"
        style={{ width: '100%', boxSizing: 'border-box', marginTop: '7px', fontSize: '12.5px', fontFamily: 'inherit', padding: '6px 8px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--panel)', color: 'var(--text)' }} />
      <div style={{ display: 'flex', gap: '7px', marginTop: '9px' }}>
        <button style={{ ...btnFort, padding: '5px 11px', fontSize: '11.5px', opacity: qui ? 1 : 0.5 }} disabled={!qui || busy}
          onClick={async () => {
            setBusy(true);
            try {
              const r = await fetch(`${BASE}/besoins/${besoinId}/candidates/${candidateId}/qualification`, {
                method: 'POST', headers: jsonH(), body: JSON.stringify({ qualifieur_id: Number(qui), message: mot || null }),
              });
              const j = await r.json().catch(() => ({}));
              if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
              showNotification?.(j.message || 'Demande envoyée.', 'success');
              setOuvert(false); setQui(''); setMot('');
              await charger(); onChange?.(j);
            } catch (e) { showNotification?.(e.message, 'error'); }
            finally { setBusy(false); }
          }}>
          Envoyer la demande
        </button>
        <button style={{ ...btn, padding: '5px 11px', fontSize: '11.5px' }} onClick={() => setOuvert(false)}>Annuler</button>
      </div>
    </div>
  );
}


/**
 * L'ÉCRAN DE SUIVI — « est-ce que ça avance ? ».
 *
 * Demande 16.09 : « un écran de suivi des qualifications en cours, dispo au
 * commercial (business manager) et à l'admin ».
 *
 * LA PORTÉE EST DÉCIDÉE PAR LE SERVEUR, pas ici : un administrateur voit
 * tout, un commercial voit les besoins dont il est responsable. L'écran
 * AFFICHE ce que le serveur lui dit de sa portée (`portee_refus`) — il ne le
 * devine pas, et il ne cache rien qu'on lui aurait servi.
 */
/**
 * OK OU KO — le retour de celui qui a demandé la qualification (17.09).
 *
 * C'est la seconde signature, et elle boucle la boucle : la personne qui a
 * qualifié apprend que son travail est accepté — ou POURQUOI il ne l'est pas,
 * ce qui est la seule façon d'ajuster la fois d'après.
 *
 * Le motif est demandé AVANT l'envoi pour un « ko » : le serveur refuse sans,
 * autant le demander que d'afficher son refus. Et seul le demandeur voit ces
 * boutons — le serveur le vérifie, l'écran ne fait que ne pas les proposer à
 * tort (`attente_retour` est calculé côté serveur).
 */
function RetourDemandeur({ id, showNotification, onDonne }) {
  const [refus, setRefus] = useState(false);   // saisie du motif ouverte
  const [motif, setMotif] = useState('');
  const [busy, setBusy] = useState(false);

  const envoyer = async (retour, raison = null) => {
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/qualifications/${id}/retour`, {
        method: 'POST', headers: jsonH(),
        body: JSON.stringify({ retour, ...(raison ? { motif: raison } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      // Le serveur NOMME qui doit trancher quand ce n'est pas nous : son
      // texte fait foi, on ne le résume pas.
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Retour enregistré.', 'success');
      setRefus(false); setMotif('');
      onDonne?.();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: '9px', paddingTop: '9px', borderTop: '1px solid var(--hair)' }}>
      <p style={{ margin: '0 0 7px', fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
        Vous aviez demandé cette qualification : <b>validez-la ou refusez-la</b>. La personne qui l'a faite sera prévenue.
      </p>
      {!refus ? (
        <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
          <button style={{ ...btnFort, padding: '5px 11px', fontSize: '11.5px' }} disabled={busy}
            onClick={() => envoyer('ok')}>
            <BI icon="check2" /> Valider
          </button>
          <button style={{ ...btn, padding: '5px 11px', fontSize: '11.5px' }} disabled={busy}
            onClick={() => setRefus(true)}>
            <BI icon="x" /> Refuser
          </button>
        </div>
      ) : (
        <div>
          <label htmlFor={`q-ko-${id}`} style={{ display: 'block', fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '4px' }}>
            Pourquoi ce n'est pas bon *
          </label>
          <input id={`q-ko-${id}`} value={motif} autoFocus
            placeholder="Prétentions hors budget, CV à retravailler…"
            onChange={e => setMotif(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && motif.trim()) envoyer('ko', motif.trim()); }}
            style={{ width: '100%', boxSizing: 'border-box', fontSize: '12.5px', fontFamily: 'inherit', padding: '7px 9px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--panel)', color: 'var(--text)', marginBottom: '8px' }} />
          <div style={{ display: 'flex', gap: '7px' }}>
            <button style={{ ...btnFort, padding: '5px 11px', fontSize: '11.5px', opacity: motif.trim() ? 1 : 0.5 }}
              disabled={busy || !motif.trim()} onClick={() => envoyer('ko', motif.trim())}>
              {busy ? <><BI icon="arrow-repeat" /> Enregistrement…</> : 'Refuser et prévenir'}
            </button>
            <button style={{ ...btn, padding: '5px 11px', fontSize: '11.5px' }} onClick={() => { setRefus(false); setMotif(''); }}>Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * LE RAPPORT D'UNE QUALIFICATION — « comment je vois le rapport ? » (17.09).
 *
 * Le suivi disait où en est chaque demande, jamais ce qu'elle CONTIENT : il
 * fallait retrouver le candidat, puis le besoin, puis rouvrir le couple. Le
 * rapport se déplie SUR la ligne — pas dans une modale, pas sur un autre
 * écran : on lit, on referme, on passe à la suivante.
 *
 * Le serveur le calcule (GET /qualifications/:id), avec les mêmes fonctions
 * que la fiche. L'écran n'additionne rien : deux calculs finiraient par
 * afficher « 4/6 » ici en face d'un « 5/6 » là.
 */
// `encadre: false` — le rapport posé DANS une couche flottante n'apporte ni
// bordure ni marge : la couche porte déjà les siennes, et deux cadres
// imbriqués se lisent comme deux panneaux. Le contenu ne décide pas de son
// contenant.
function RapportQualification({ id, showNotification, onChange, encadre = true }) {
  const [vue, setVue] = useState(null);   // null = chargement | { … } | { erreur }
  const [tick, setTick] = useState(0);    // relecture après un retour donné

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/qualifications/${id}`, { headers: authH() });
        if ([404, 501].includes(r.status)) { if (!annule) setVue({ aVenir: true }); return; }
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
        if (!annule) setVue(j);
      } catch (e) { if (!annule) setVue({ erreur: e.message }); }
    })();
    return () => { annule = true; };
  }, [id, tick]);

  const cadre = encadre
    ? { marginTop: '9px', padding: '11px 13px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--panel)' }
    : { padding: '12px 14px' };
  if (vue == null) return <div style={cadre}><span style={{ fontSize: '12px', color: 'var(--text-faint)' }}>Ouverture du rapport…</span></div>;
  if (vue.aVenir) return <div style={cadre}><span style={{ fontSize: '12px', color: 'var(--text-2)' }}>Le rapport arrive côté serveur.</span></div>;
  if (vue.erreur) return <div style={cadre}><span style={{ fontSize: '12px', color: 'var(--erp-danger)' }}>{vue.erreur}</span></div>;

  const d = vue.demande || {};
  return (
    <div style={cadre}>
      {/* CE QUE LE RAPPORT DIT D'ABORD : le verdict et sa signature. C'est la
          question posée (« qualifié par le user X »), pas une ligne de fin. */}
      <div style={{ fontSize: '12.5px', color: 'var(--text-strong)', fontWeight: 600 }}>
        {d.verdict_libelle
          ? <>{d.verdict_libelle} par {d.qualifieur_nom || `#${d.qualifieur_id}`}{d.terminee_le ? ` le ${new Date(d.terminee_le).toLocaleDateString('fr-FR')}` : ''}</>
          : <>En cours chez {d.qualifieur_nom || `#${d.qualifieur_id}`} — demandée par {d.demandee_par_nom || '—'}</>}
      </div>
      {d.motif && <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.5 }}>{d.motif}</p>}
      {d.message && <p style={{ margin: '4px 0 0', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>Demande : « {d.message} »</p>}

      <div style={{ marginTop: '9px' }}>
        {(vue.etapes || []).map(e => (
          <div key={e.cle} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '5px 0', borderTop: '1px solid var(--hair)' }}>
            <BI icon={e.sans_objet ? 'dash-circle' : e.acquise ? 'check-circle-fill' : 'circle'}
              style={{ marginTop: '2px', flex: 'none', fontSize: '11px',
                color: e.sans_objet ? 'var(--text-faint)' : e.acquise ? 'var(--erp-success)' : 'var(--text-faint)' }} />
            <span style={{ flex: 1, minWidth: 0, fontSize: '12px', color: e.acquise ? 'var(--text-2)' : 'var(--text-3)' }}>
              {e.libelle}
              {e.sans_objet && <span style={{ color: 'var(--text-3)' }}> — sans objet{e.sans_objet.motif ? ` : ${e.sans_objet.motif}` : ''}</span>}
              {/* D'OÙ vient l'état : une case cochée à la main et un document
                  déposé ne valent pas la même chose à la relecture. */}
              {!e.sans_objet && e.coche_a_la_main && <span style={{ color: 'var(--text-3)' }}> — coché à la main{e.coche_a_la_main.note ? ` (${e.coche_a_la_main.note})` : ''}</span>}
              {!e.sans_objet && e.validation_requise && e.fait && !e.validee && <span style={{ color: 'var(--text-3)' }}>{e.relu_perime ? ' — modifié depuis la relecture' : ' — pas encore relu'}</span>}
            </span>
          </div>
        ))}
      </div>

      {!vue.complet && vue.restantes?.length > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
          Il reste : {vue.restantes.join(', ')}.
        </p>
      )}

      {/* LE RETOUR DE CELUI QUI A DEMANDÉ — la seconde signature. Sans elle,
          « qualifié » veut dire « la recruteuse pense que c'est bon », pas
          « on l'envoie au client ». */}
      {d.retour && (
        <p style={{ margin: '9px 0 0', paddingTop: '8px', borderTop: '1px solid var(--hair)', fontSize: '12px', lineHeight: 1.5,
          color: d.retour === 'ok' ? 'var(--erp-success)' : 'var(--text-2)' }}>
          <BI icon={d.retour === 'ok' ? 'check2-circle' : 'x-circle'} />{' '}
          <b>{d.retour_libelle}</b>{d.retour_par_nom ? ` — ${d.retour_par_nom}` : ''}
          {d.retour_le ? ` le ${new Date(d.retour_le).toLocaleDateString('fr-FR')}` : ''}
          {d.retour_motif ? ` : ${d.retour_motif}` : ''}
        </p>
      )}
      {d.attente_retour && (
        <RetourDemandeur id={id} showNotification={showNotification}
          onDonne={() => { setTick(t => t + 1); onChange?.(); }} />
      )}
    </div>
  );
}

export function SuiviQualifications({ onOuvrirCandidat, showNotification }) {
  const [vue, setVue] = useState(null);   // null = chargement | { demandes… } | { erreur }
  const [filtre, setFiltre] = useState('en_cours');
  const [qui, setQui] = useState(null);   // qualifieur_id — recadrage sur une personne
  const [rapport, setRapport] = useState(null); // id de la demande dépliée

  useEffect(() => {
    let annule = false;
    (async () => {
      setVue(null);
      try {
        const p = new URLSearchParams();
        if (filtre !== 'tout') p.set('statut', filtre);
        if (qui != null) p.set('qualifieur_id', String(qui));
        const q = p.toString() ? `?${p}` : '';
        const r = await fetch(`${BASE}/qualifications/suivi${q}`, { headers: authH() });
        // 403 et 404 ne se disent pas pareil, et un 404 muet est le pire des
        // deux : `{ interdit: false }` ne déclenchait AUCUN rendu — écran
        // blanc, sans liste, sans état vide, sans message. Un écran qui ne
        // dit rien fait chercher ce qu'on a mal fait.
        if (r.status === 403) { if (!annule) setVue({ interdit: true }); return; }
        if ([404, 501].includes(r.status)) { if (!annule) setVue({ aVenir: true }); return; }
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
        if (!annule) setVue(j);
      } catch (e) { if (!annule) setVue({ erreur: e.message }); }
    })();
    return () => { annule = true; };
  }, [filtre, qui]);

  const chip = (v, libelle) => (
    <button key={v} type="button" onClick={() => setFiltre(v)}
      style={{ ...btn, padding: '5px 11px', fontSize: '12px',
        borderColor: filtre === v ? 'var(--text-strong)' : 'var(--input-border)',
        fontWeight: filtre === v ? 700 : 600 }}>{libelle}</button>
  );

  return (
    <div {...ancre('qualifications-liste')} style={{ padding: '18px', fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '11px', marginBottom: '14px' }}>
        <h2 style={{ margin: 0, fontSize: '19px', fontWeight: 800, color: 'var(--text-strong)' }}>Qualifications</h2>
        {vue?.demandes && (
          <span style={{ fontSize: '12px', color: 'var(--text-2)' }}>
            {vue.en_cours} en cours
            {vue.en_retard > 0 && <> · <b style={{ color: 'var(--erp-danger)' }}>{vue.en_retard} en retard</b></>}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {chip('en_cours', 'En cours')}
        {/* CE QUI ATTEND LE DEMANDEUR. Une qualification conclue sans retour
            sortait des « en cours » et n'apparaissait plus nulle part : la
            boucle restait ouverte et personne ne le voyait. */}
        {chip('attente_retour', 'À valider')}
        {chip('qualifiee', 'Qualifiés')}{chip('refusee', 'Écartés')}{chip('tout', 'Tout')}
      </div>

      {/* Le serveur dit ce qu'il a servi : on le répète, plutôt que de laisser
          croire que la liste est celle de toute l'agence. */}
      {vue?.portee_refus && (
        <p style={{ fontSize: '11.5px', color: 'var(--text-3)', margin: '0 0 12px', lineHeight: 1.5 }}>{vue.portee_refus}</p>
      )}

      {/* QUI PORTE QUOI — la première question d'un responsable n'est pas
          « quelles demandes ? » mais « qui est débordé, et qui n'a pas ouvert
          les siennes ? ». Un clic recadre la liste sur la personne : voir
          qu'elle a quatre retards sans pouvoir les lire n'avance à rien. */}
      {vue?.par_personne?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', marginBottom: '13px' }}>
          {qui != null && (
            <button type="button" onClick={() => setQui(null)} style={{ ...btn, padding: '5px 10px', fontSize: '11.5px' }}>
              <BI icon="arrow-left" /> Tout le monde
            </button>
          )}
          {vue.par_personne.map(p => (
            <button key={p.qualifieur_id} type="button" onClick={() => setQui(qui === p.qualifieur_id ? null : p.qualifieur_id)}
              title={`${p.en_cours} en cours${p.pas_ouvertes ? ` · ${p.pas_ouvertes} pas encore ouverte(s)` : ''}`}
              style={{ ...btn, padding: '5px 10px', fontSize: '11.5px', gap: '7px',
                borderColor: qui === p.qualifieur_id ? 'var(--text-strong)' : 'var(--input-border)' }}>
              <span style={{ fontWeight: 700 }}>{p.nom}</span>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: 'var(--text-3)' }}>{p.en_cours}</span>
              {p.en_retard > 0 && (
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, padding: '0 5px', borderRadius: '4px', background: 'var(--erp-danger-soft)', color: 'var(--erp-danger)' }}>{p.en_retard}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Le serveur dit quand il a dû s'arrêter de compter — plutôt que de
          laisser des lignes à « 0/6 » qu'on croirait au point mort. */}
      {vue?.avancement_partiel && (
        <p style={{ fontSize: '11.5px', color: 'var(--text-3)', margin: '0 0 12px', lineHeight: 1.5 }}>{vue.avancement_partiel}</p>
      )}

      {vue == null && <p style={{ fontSize: '13px', color: 'var(--text-faint)' }}>Chargement…</p>}
      {vue?.interdit && <p style={{ fontSize: '13px', color: 'var(--text-2)' }}>Cet écran est réservé aux responsables de besoins et aux administrateurs.</p>}
      {vue?.aVenir && <p style={{ fontSize: '13px', color: 'var(--text-2)' }}>Le suivi des qualifications arrive côté serveur — rien n'est perdu, l'écran s'affichera dès qu'il répondra.</p>}
      {vue?.erreur && <p style={{ fontSize: '13px', color: 'var(--erp-danger)' }}>{vue.erreur}</p>}
      {/* Filet : une réponse qui ne porte pas `demandes` (forme inattendue,
          serveur plus ancien) ne doit pas produire un écran muet — c'est le
          symptôme qu'on vient de corriger, pas une hypothèse. */}
      {vue && !vue.interdit && !vue.aVenir && !vue.erreur && !Array.isArray(vue.demandes) && (
        <p style={{ fontSize: '13px', color: 'var(--text-2)' }}>Le serveur a répondu sans la liste des qualifications — rechargez l'écran.</p>
      )}
      {vue?.demandes?.length === 0 && (
        <p style={{ fontSize: '13px', color: 'var(--text-faint)' }}>
          {filtre === 'attente_retour'
            ? "Rien n'attend votre retour. Une qualification conclue apparaît ici jusqu'à ce que vous la validiez ou la refusiez."
            : <>Aucune qualification {filtre === 'en_cours' ? 'en cours' : 'à afficher'}. Une demande se lance depuis la carte d'un candidat sur un besoin.</>}
        </p>
      )}

      {vue?.demandes?.map(d => (
        <div key={d.id} style={{ padding: '11px 0', borderTop: '1px solid var(--hair)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '11px' }}>
          <BI icon={d.verdict === 'qualifie' ? 'patch-check-fill' : d.verdict === 'refuse' ? 'slash-circle' : d.en_retard ? 'exclamation-triangle-fill' : 'hourglass-split'}
            style={{ marginTop: '3px', flex: 'none',
              color: d.verdict === 'qualifie' ? 'var(--erp-success)' : d.en_retard ? 'var(--erp-danger)' : 'var(--text-3)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>
              {onOuvrirCandidat
                ? <button type="button" onClick={() => onOuvrirCandidat(d.candidate_id)}
                    style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}>{d.candidat}</button>
                : d.candidat}
              <span style={{ fontWeight: 400, color: 'var(--text-2)' }}> — {d.besoin}{d.client ? ` · ${d.client}` : ''}</span>
            </div>
            <div style={{ fontSize: '11.5px', color: 'var(--text-3)', marginTop: '2px' }}>
              {d.en_cours
                ? <>Confiée à <b>{d.qualifieur}</b> par {d.demandeur} · depuis {d.jours} jour{d.jours > 1 ? 's' : ''}
                    {d.reportee_a ? ` · reporté au ${new Date(d.reportee_a).toLocaleDateString('fr-FR')}` : ''}
                    {!d.repondue_le ? ' · pas encore ouverte' : ''}</>
                : <>{d.verdict_libelle} par <b>{d.qualifieur}</b>{d.terminee_le ? ` le ${new Date(d.terminee_le).toLocaleDateString('fr-FR')}` : ''}{d.motif ? ` — ${d.motif}` : ''}
                    {d.retour_libelle && <> · {d.retour_libelle}{d.retour_par_nom ? ` (${d.retour_par_nom})` : ''}{d.retour_motif ? ` — ${d.retour_motif}` : ''}</>}</>}
            </div>
            {/* OÙ ELLE EN EST. « Depuis 4 jours » fait rouvrir la fiche pour
                comprendre ; « 5/6, il manque les conditions » se relance d'un
                mot. Le compte est celui de la fiche, pas un second calcul. */}
            {d.en_cours && d.avancement && (
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                <span style={{ display: 'inline-flex', gap: '3px', flex: 'none' }} aria-hidden="true">
                  {Array.from({ length: d.avancement.total }, (_, i) => (
                    <span key={i} style={{ width: '13px', height: '4px', borderRadius: '2px',
                      background: i < d.avancement.faites ? 'var(--erp-success)' : 'var(--hair)' }} />
                  ))}
                </span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', fontWeight: 700, color: 'var(--text-2)' }}>
                  {d.avancement.faites}/{d.avancement.total}
                </span>
                {d.avancement.restantes.length > 0 && (
                  <span style={{ fontSize: '11px', color: 'var(--text-3)', minWidth: 0 }}>
                    il manque : {d.avancement.restantes.join(', ')}
                  </span>
                )}
                {/* Une dérogation se relit : sans elle, « 4/4 » sur six étapes
                    ferait croire à un compte faux. */}
                {d.avancement.sans_objet?.length > 0 && (
                  <span style={{ fontSize: '11px', color: 'var(--text-faint)', minWidth: 0 }}>
                    sans objet : {d.avancement.sans_objet.join(', ')}
                  </span>
                )}
              </div>
            )}
          </div>
          {d.en_retard && (
            <span style={{ flex: 'none', fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '5px', background: 'var(--erp-danger-soft)', color: 'var(--erp-danger)' }}>en retard</span>
          )}
          {d.attente_retour && (
            <span style={{ flex: 'none', fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '5px', background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)' }}>à valider</span>
          )}
          {/* LE RAPPORT — ce qui manquait : le suivi disait où en est chaque
              demande, jamais ce qu'elle CONTIENT. Il se déplie SUR la ligne :
              on lit, on referme, on passe à la suivante. */}
          <button type="button" onClick={() => setRapport(r => r === d.id ? null : d.id)}
            aria-expanded={rapport === d.id}
            style={{ ...btn, flex: 'none', padding: '3px 9px', fontSize: '11px' }}>
            <BI icon={rapport === d.id ? 'chevron-up' : 'file-text'} /> {rapport === d.id ? 'Fermer' : 'Voir le rapport'}
          </button>
        </div>
        {rapport === d.id && (
          <RapportQualification id={d.id} showNotification={showNotification}
            onChange={() => setFiltre(f => f)} />
        )}
        </div>
      ))}
    </div>
  );
}
